// Login por código de e-mail (porta do login.js). Com Cognito no build, o código é real (#4340);
// sem ele, a demonstração simula o envio pelo mock. O front só mostra as etapas.
import { useEffect, useRef, useState, type FormEvent } from "react";
import type { Me } from "@/api/types";
import { Icon } from "@/ui/Icon";
import { useFormPanel } from "@/ui/FormPanel";
import { demoLogin, isDemoAuth, loginError, startEmailLogin, verifyEmailLogin } from "./session";

/** O Cognito manda 8 dígitos no login sem senha; a demonstração usa 6. */
const CODE_LENGTH = isDemoAuth ? 6 : 8;

interface Challenge {
  id: string;
  email: string;
  remember: boolean;
  created: number;
  demoCode?: string;
}

export function Login({ onLogin }: { onLogin: (me: Me) => void }) {
  const [challenge, setChallenge] = useState<Challenge | null>(null);
  const { form } = useFormPanel();
  const requestAccess = () =>
    form(
      "Solicitar acesso à Iuvo",
      ["Nome completo", "E-mail profissional", "Instituição"],
      isDemoAuth ? "Solicitação demonstrativa registrada. Nenhum e-mail foi enviado." : "Solicitação registrada. A equipe Iuvo entra em contato.",
    );

  useEffect(() => {
    document.body.classList.add("login-screen");
    return () => document.body.classList.remove("login-screen");
  }, []);

  return (
    <div id="loginRoot">
      <div className="login-layout">
        <section className="login-story">
          <a className="login-brand" href="#" aria-label="Iuvo Console, início">
            iuvo<span>ai</span>
            <small>CONSOLE</small>
          </a>
          <div className="story-content">
            <span className="login-eyebrow">INTELIGÊNCIA QUE CUIDA.</span>
            <h1>
              Mais clareza.
              <br />
              Mais tempo
              <br />
              para <em>cuidar.</em>
            </h1>
            <p>
              O atendimento da sua instituição,
              <br />
              conectado a resultados que importam.
            </p>
            <div className="login-artifact">
              <div className="artifact-heading">
                <span>
                  <Icon name="pulse" /> Seu atendimento, em perspectiva
                </span>
                <span className="live-label">
                  <i /> Visão consolidada
                </span>
              </div>
              <div className="artifact-number">
                49<span>%</span>
                <small>
                  das conversas resolvidas
                  <br />
                  sem atendente
                </small>
              </div>
              <svg className="login-curve" viewBox="0 0 420 65" aria-hidden="true">
                <path d="M0 60C30 60 28 38 55 43S90 61 120 34s35-20 60-10 40 15 65-5 45 20 70 2S370 10 420 3" stroke="#5d2a1a" strokeWidth="2" fill="none" />
              </svg>
              <div className="artifact-footer">
                <span>Mais autonomia no atendimento</span>
                <b>
                  <Icon name="external" /> 4 pontos nesta semana
                </b>
              </div>
            </div>
          </div>
          <div className="story-footer">
            Tecnologia a serviço do cuidado.
            <a href="https://iuvoai.com.br" target="_blank" rel="noopener noreferrer">
              iuvoai.com.br <Icon name="external" />
            </a>
          </div>
        </section>
        <section className="login-form-side">
          <div className="login-topline">
            Portal de atendimento{" "}
            <span>
              <Icon name="lock" /> Acesso individual
            </span>
          </div>
          <div className="login-form-wrap">
            {challenge ? (
              <CodeStep challenge={challenge} onBack={() => setChallenge(null)} onLogin={onLogin} onResend={setChallenge} requestAccess={requestAccess} />
            ) : (
              <EmailStep onChallenge={setChallenge} onLogin={onLogin} requestAccess={requestAccess} />
            )}
          </div>
          <div className="login-bottom">
            <span>© 2026 Iuvo. Todos os direitos reservados.</span>
          </div>
        </section>
      </div>
    </div>
  );
}

function EmailStep(props: { onChallenge: (c: Challenge) => void; onLogin: (me: Me) => void; requestAccess: () => void }) {
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const input = e.currentTarget.elements.namedItem("loginEmail") as HTMLInputElement;
    const remember = (e.currentTarget.elements.namedItem("rememberLogin") as HTMLInputElement).checked;
    if (!input.validity.valid) {
      setError("Informe um e-mail válido para continuar.");
      input.focus();
      return;
    }
    setBusy(true);
    try {
      const email = input.value.trim().toLowerCase();
      const c = await startEmailLogin(email);
      props.onChallenge({ id: c.challengeId, email, remember, created: Date.now(), demoCode: c.demoCode });
    } catch (err) {
      setError(isDemoAuth ? "Não foi possível enviar o código agora. Tente novamente." : loginError(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <>
      <span className="login-step">BEM-VINDO À IUVO</span>
      <h2>
        Um bom dia
        <br />
        começa por aqui.
      </h2>
      <p className="login-intro">Entre com seu e-mail. Sem senha, sem complicação.</p>
      <form onSubmit={submit} noValidate>
        <div className="login-field">
          <label htmlFor="loginEmail">E-mail profissional</label>
          <div className="input-wrap">
            <Icon name="mail" />
            <input
              id="loginEmail"
              name="loginEmail"
              type="email"
              autoComplete="username"
              placeholder={isDemoAuth ? "marina@vitalis.example" : "nome@instituicao.com.br"}
              required
              aria-describedby="loginError"
              aria-invalid={error ? true : undefined}
            />
          </div>
        </div>
        <div className="login-options">
          <label className="remember">
            <input id="rememberLogin" name="rememberLogin" type="checkbox" /> Manter conectado neste navegador
          </label>
        </div>
        <div id="loginError" className="login-error" role="alert" aria-live="polite">
          {error}
        </div>
        <button type="submit" className="login-primary" disabled={busy}>
          <span>Receber código por e-mail</span>
          <Icon name="right" />
        </button>
      </form>
      {isDemoAuth && (
        <>
          <div className="login-divider">
            <span>ou conheça a plataforma</span>
          </div>
          <button className="login-demo" onClick={async () => props.onLogin(await demoLogin())}>
            Explorar demonstração <Icon name="right" />
          </button>
          <div className="demo-notice">
            <Icon name="info" />
            <span>
              Envio simulado. Use marina@vitalis.example.
              <br />O código será exibido na próxima etapa.
            </span>
          </div>
        </>
      )}
      <p className="login-access">
        Ainda não tem acesso?{" "}
        <button onClick={props.requestAccess}>
          Solicitar à Iuvo <Icon name="external" />
        </button>
      </p>
    </>
  );
}

function CodeStep(props: {
  challenge: Challenge;
  onBack: () => void;
  onLogin: (me: Me) => void;
  onResend: (c: Challenge) => void;
  requestAccess: () => void;
}) {
  const { challenge } = props;
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [now, setNow] = useState(Date.now());
  const input = useRef<HTMLInputElement>(null);
  useEffect(() => {
    input.current?.focus();
    const t = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(t);
  }, []);
  const wait = Math.max(0, 30 - Math.floor((now - challenge.created) / 1000));

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (code.length !== CODE_LENGTH) {
      setError(`Digite os ${CODE_LENGTH} dígitos do código.`);
      return;
    }
    setBusy(true);
    try {
      props.onLogin(await verifyEmailLogin({ id: challenge.id, email: challenge.email }, code, challenge.remember));
    } catch (err) {
      setError(loginError(err));
      setCode("");
      input.current?.focus();
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button className="login-text-link" onClick={props.onBack}>
        <Icon name="left" /> Alterar e-mail
      </button>
      <div className="otp-heading">
        <Icon name="mail" />
      </div>
      <span className="login-step">CONFIRME SEU ACESSO</span>
      <h2>
        Seu código.
        <br />
        Seu próximo passo.
      </h2>
      <p className="login-intro">
        Se houver um acesso válido, um código será enviado para <b>{challenge.email}</b>.
      </p>
      <form id="codeForm" onSubmit={submit} noValidate>
        <label htmlFor="loginCode">Código de {CODE_LENGTH} dígitos</label>
        <input
          ref={input}
          id="loginCode"
          className="otp-input"
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={CODE_LENGTH}
          placeholder={"0".repeat(CODE_LENGTH)}
          aria-describedby="codeError"
          aria-invalid={error ? true : undefined}
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, CODE_LENGTH))}
        />
        <p className="muted otp-expiry">{isDemoAuth ? "Válido por 10 minutos. Até 5 tentativas." : "Válido por alguns minutos. Confira também a caixa de spam."}</p>
        <div id="codeError" role="alert" className="login-error">
          {error}
        </div>
        <button className="login-primary" type="submit" disabled={busy}>
          Confirmar e entrar <Icon name="right" />
        </button>
      </form>
      <div className="otp-resend">
        <span>Não recebeu?</span>
        <button
          type="button"
          className="login-text-link"
          disabled={wait > 0}
          onClick={async () => {
            try {
              const c = await startEmailLogin(challenge.email);
              props.onResend({ ...challenge, id: c.challengeId, created: Date.now(), demoCode: c.demoCode });
              setError(c.demoCode ? `Novo código demonstrativo disponível: ${c.demoCode}` : "Enviamos um novo código.");
            } catch (err) {
              setError(loginError(err));
            }
          }}
        >
          {wait ? `Reenviar em ${wait}s` : "Reenviar código"}
        </button>
      </div>
      {challenge.demoCode && (
        <div className="otp-demo">
          <b>Demonstração · sem envio real</b>
          <p>
            Código para testar: <code>{challenge.demoCode}</code>
          </p>
          <small>Contas fictícias: marina@vitalis.example ou rafael@iuvo.example.</small>
        </div>
      )}
      <p className="login-access">
        Problemas com o acesso?{" "}
        <button onClick={props.requestAccess}>
          Falar com a Iuvo <Icon name="external" />
        </button>
      </p>
    </>
  );
}
